import { Box, Grid } from '@mui/material';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridColumn as Column,
  Grid as KendoGrid
} from '@progress/kendo-react-grid';
import { sumBy } from 'lodash';
import React, { Fragment, RefObject, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionButton,
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { DateCell } from 'src/components/shared/DataGrid/DateCell';
import { SelectionGrid } from 'src/components/shared/DataGrid/SelectionGrid/SelectionGrid';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header3 } from 'src/components/shared/Typography/Header3';
import { useReduxDispatch } from 'src/store';
import { alertError, alertSuccess } from 'src/store/alerts/alerts.actions';
import { MixtureCandidateDto } from 'src/store/mixturecandidates/mixturecandidate';
import {
  deleteMixtureCandidate,
  fetchMixtureCandidates
} from 'src/store/mixturecandidates/mixturecandidates.actions';
import { selectMixtureCandidates } from 'src/store/mixturecandidates/mixturecandidates.selectors';
import {
  MixtureDetailDto,
  MixtureProductDto
} from 'src/store/mixtures/mixture';
import { MixtureApi } from 'src/store/mixtures/mixtures.api';
import { ParameterSetDetailDto } from 'src/store/parametersets/parameterset';
import {
  getCorrectedValueAtIndex,
  getCorrectedValueAtIndexFormatted,
  isParameterOk
} from 'src/store/products/product.utils';
import { ArrayUtils } from 'src/utils/array';

interface Props {
  mixture: Nullable<MixtureDetailDto>;
  parameterSet: Nullable<ParameterSetDetailDto>;
  mixtureProducts: MixtureProductDto[];
  onMixtureChange: (mixture: MixtureDetailDto) => void;
  onMixtureProductsChanged: (mixtureProducts: MixtureProductDto[]) => void;
  initExporter: (exporter: ExcelExport) => void;
  onExport: () => void;
  showExportSpinner: boolean;
  gridRef: RefObject<KendoGrid>;
}

export const MixtureProducts: React.FC<Props> = ({
  mixture,
  parameterSet,
  mixtureProducts,
  onMixtureChange,
  onMixtureProductsChanged,
  initExporter,
  onExport,
  showExportSpinner,
  gridRef
}) => {
  const { t } = useTranslation();
  const dispatch = useReduxDispatch();

  const [amount, setAmount] = useState<Nullable<number>>(null);
  const [amountError, setAmountError] = useState<string>();

  const [selectedMixtureProduct, setSelectedMixtureProduct] =
    useState<Nullable<MixtureProductDto>>(null);

  const [selectedMixtureCandidate, setSelectedMixtureCandidate] =
    useState<Nullable<MixtureCandidateDto>>(null);

  const [mixtureProduct, setMixtureProduct] =
    useState<Nullable<MixtureProductDto>>(null);

  const [mixtureCandidate, setMixtureCandidate] =
    useState<Nullable<MixtureCandidateDto>>(null);

  const mixtureCandidates = useSelector(selectMixtureCandidates);

  useEffect(() => {
    onMixtureProductsChanged(mixtureProducts);
  }, [mixtureProducts, onMixtureProductsChanged]);

  const onRemoveMixtureCandidate = async (product: MixtureCandidateDto) => {
    setMixtureCandidate(product);
  };

  const onRemoveMixtureCandidateCancel = async () => {
    setMixtureCandidate(null);
  };

  const onRemoveMixtureCandidateConfirm = async () => {
    if (mixtureCandidate) {
      await dispatch(
        deleteMixtureCandidate(mixtureCandidate.id, mixtureCandidate)
      );
      setMixtureCandidate(null);
    }
  };

  const onDeleteMixtureProduct = async (product: MixtureProductDto) => {
    setMixtureProduct(product);
  };

  const onDeleteMixtureProductCancel = async () => {
    setMixtureProduct(null);
  };

  const onAddMixtureProduct = async () => {
    if (mixture && selectedMixtureCandidate) {
      const { ok, error } = await MixtureApi.addMixtureProduct(
        mixture.id!,
        selectedMixtureCandidate.productId,
        amount
      );

      if (ok) {
        dispatch(alertSuccess(t('mixture.productAdded')));
      } else {
        dispatch(
          alertError(error?.errors?.detail ?? t('mixture.productAddedFailed'))
        );
      }

      setSelectedMixtureCandidate(null);
      setAmount(null);
    }
  };

  const deleteMixtureProduct = (
    mixture: MixtureDetailDto,
    mixtureProduct: MixtureProductDto
  ) => {
    const newMixtureProducts = ArrayUtils.deleteFromListById(
      mixture.mixtureProducts,
      mixtureProduct
    );
    onMixtureChange({
      ...mixture,
      currentAmount: sumBy(newMixtureProducts, m => m.amount),
      currentNumber: newMixtureProducts.length,
      mixtureProducts: newMixtureProducts
    });
  };

  const onDeleteMixtureProductConfirm = async () => {
    if (mixture && mixtureProduct) {
      const { ok, error } = await MixtureApi.deleteMixtureProduct(
        mixture.id!,
        mixtureProduct
      );

      if (ok) {
        dispatch(alertSuccess(t('mixture.productDeleted')));
        deleteMixtureProduct(mixture, mixtureProduct);
      } else {
        dispatch(alertError(error?.errors?.detail ?? t('delete.failed')));
      }
      setMixtureProduct(null);
    }
  };

  const onRemoveMixtureProduct = async () => {
    if (mixture && selectedMixtureProduct) {
      const { ok, error } = await MixtureApi.removeMixtureProduct(
        mixture.id!,
        selectedMixtureProduct
      );

      if (ok) {
        dispatch(alertSuccess(t('mixture.productDeleted')));
        deleteMixtureProduct(mixture, selectedMixtureProduct);
      } else {
        dispatch(alertError(error?.errors?.detail ?? t('delete.failed')));
      }

      await dispatch(fetchMixtureCandidates(mixture.parameterSetId));
      setSelectedMixtureProduct(null);
    }
  };

  return (
    <Fragment>
      <Grid container>
        <Grid item xs={12} lg={12}>
          <Box
            className="mb-24"
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Header3 className="mr-24">{t('mixture.products')}</Header3>
            <SecondaryButton
              onClick={onExport}
              loading={showExportSpinner}
              icon="la-file-excel"
              className="mr-32"
            >
              {`${t('export.excel')}`}
            </SecondaryButton>
          </Box>
          <ExcelExport data={mixtureProducts} ref={initExporter}>
            <SelectionGrid
              ref={gridRef}
              items={mixtureProducts}
              onItemSelected={item => setSelectedMixtureProduct(item)}
              filterable={false}
              style={{ marginBottom: 24 }}
            >
              <Column field="runNo" title={t('product.runNo')} width="120px" />
              <Column
                field="runSystemName"
                title={t('runSystem.singular')}
                width="240px"
              />
              <Column
                field="gradeName"
                title={t('grade.singular')}
                width="240px"
              />
              <Column field="lotNo" title={t('product.lotNo')} width="120px" />
              <Column field="amount" title={t('props.amount')} width="100px" />
              <Column
                field="userInitials"
                title={t('user.singular')}
                width="100px"
              />
              {parameterSet &&
                (parameterSet.parameterSetParameters ?? []).map(
                  (item, index) => (
                    <Column
                      field={`parameter${item.parameterId}`}
                      title={`${item.parameterName} - ${item.methodName}`}
                      width="140px"
                      filterable={false}
                      key={item.id}
                      cell={({ dataItem }: { dataItem: MixtureProductDto }) => (
                        <td
                          style={{
                            color: isParameterOk(
                              getCorrectedValueAtIndex(
                                dataItem.parameters,
                                index
                              ),
                              dataItem.parameters[index]
                            )
                              ? 'var(--green-status-color)'
                              : 'var(--red-status-color)',
                            textAlign: 'right'
                          }}
                        >
                          {getCorrectedValueAtIndexFormatted(
                            dataItem.parameters,
                            index
                          )}
                        </td>
                      )}
                    />
                  )
                )}
              <Column
                width="100px"
                cell={props => (
                  <td>
                    <DeleteButton
                      onClick={() => {
                        onDeleteMixtureProduct(props.dataItem);
                      }}
                    />
                  </td>
                )}
              />
            </SelectionGrid>
          </ExcelExport>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item lg={3}>
          <InputField
            type="number"
            name="amount"
            label={t('props.amount')}
            value={amount}
            onChange={event => {
              const amount = event.target.value;
              setAmount(Number(amount));
              if (amount) setAmountError('');
            }}
            disabled={!mixture || !selectedMixtureCandidate}
            displayError={!!amountError}
            errorMessage={amountError}
            className="mb-24"
          />
        </Grid>
        <Grid item lg={1}>
          <ActionButton
            icon={'la la-arrow-up'}
            onClick={onAddMixtureProduct}
            disabled={!selectedMixtureCandidate || !mixture || amount == null}
          />
        </Grid>
        <Grid item lg={1}>
          <ActionButton
            icon={'la la-arrow-down'}
            onClick={onRemoveMixtureProduct}
            disabled={!selectedMixtureProduct || !mixture}
          />
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12} lg={12}>
          <Header3 className="mb-24">{t('mixture.candidates')}</Header3>
          <SelectionGrid
            items={mixtureCandidates}
            onItemSelected={item => setSelectedMixtureCandidate(item)}
            filterable={false}
          >
            <Column field="runNo" title={t('product.runNo')} width="120px" />
            <Column
              field="runSystemName"
              title={t('runSystem.singular')}
              width="240px"
            />
            <Column
              field="gradeName"
              title={t('grade.singular')}
              width="240px"
            />
            <Column field="lotNo" title={t('product.lotNo')} width="120px" />
            <Column field="mixtureNo" title={t('props.number')} width="100px" />
            <Column
              field="mixDate"
              title={t('mixture.mixDate')}
              width="100px"
              filterable={false}
              cell={props => <DateCell {...props} />}
            />
            {parameterSet &&
              (parameterSet.parameterSetParameters ?? []).map((item, index) => (
                <Column
                  field={`parameter${item.parameterId}`}
                  title={`${item.parameterName} - ${item.methodName}`}
                  width="140px"
                  filterable={false}
                  key={item.id}
                  cell={({ dataItem }) => (
                    <td
                      style={{
                        color: dataItem.parameters
                          ? isParameterOk(
                              getCorrectedValueAtIndex(
                                dataItem.parameters,
                                index
                              ),
                              dataItem
                            )
                            ? 'var(--green-status-color)'
                            : 'var(--red-status-color)'
                          : 'var(--green-status-color)',
                        textAlign: 'right'
                      }}
                    >
                      {dataItem.parameters
                        ? getCorrectedValueAtIndexFormatted(
                            dataItem.parameters,
                            index
                          )
                        : null}
                    </td>
                  )}
                />
              ))}
            <Column
              width="100px"
              cell={props => (
                <td>
                  <ActionButton
                    icon="la la-trash"
                    onClick={() => onRemoveMixtureCandidate(props.dataItem)}
                  />
                </td>
              )}
            />
          </SelectionGrid>
        </Grid>
      </Grid>

      <Modal
        width={500}
        header={`${t('remove.verb')} ${t('product.singular')}`}
        confirmText={t('remove.verb')}
        onClose={onDeleteMixtureProductCancel}
        onConfirm={onDeleteMixtureProductConfirm}
        open={!!mixtureProduct}
        showActions
      >
        <BodyType>
          {t('product.delete')} <q>{mixtureProduct?.runNo}</q>?
        </BodyType>
      </Modal>

      <Modal
        width={500}
        header={`${t('remove.verb')} ${t('mixtureCandidate.singular')}`}
        confirmText={t('remove.verb')}
        onClose={onRemoveMixtureCandidateCancel}
        onConfirm={onRemoveMixtureCandidateConfirm}
        open={!!mixtureCandidate}
        showActions
      >
        <BodyType>
          {t('mixtureCandidate.delete')} <q>{mixtureCandidate?.runNo}</q>?
        </BodyType>
      </Modal>
    </Fragment>
  );
};
